import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { GripVertical, Pencil, Trash2, Plus, X } from 'lucide-react';
import HelpDialog from './HelpDialog';
import axios from 'axios';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSettings } from '@/contexts/SettingsContext';
import LinkedListPicker from './LinkedListPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HELP_CONTENT = `**Contexts allow you to organize your tasks into separate workspaces.**

Each Context has its own set of tags (Type, Domain, and Energy), its own Task Storage, and its own Archive. This is useful when you want to keep different areas of your life completely separate.

**For example:**
- Create a "Work" context for professional tasks with work-specific tags
- Create a "Personal" context for home and life tasks
- Create a "Side Project" context for hobby or creative work

**Tips:**
- You can rename contexts to match your workflow
- Drag and drop to reorder contexts (affects priority in "Show All" view)
- Each context starts with the default set of tags, which you can customize
- Assignee tags are shared across all contexts

**Maximum of 5 contexts allowed.**`;

function SortableContextItem({ context, onEdit, onDelete, isEditing, editValue, setEditValue, onSaveEdit, onCancelEdit, dragHandlePosition, activeLists, onLinkChange }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: context.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const dragHandle = (
    <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing flex-shrink-0">
      <GripVertical size={18} className="text-muted-foreground" />
    </div>
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex flex-col gap-1 p-3 bg-muted rounded-lg"
      data-testid={`context-item-${context.id}`}
    >
      <div className="flex items-center gap-2">
        {dragHandlePosition === 'left' && dragHandle}
        {isEditing ? (
          <>
            <Input
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onSaveEdit();
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  onCancelEdit();
                }
              }}
              className="flex-1"
              autoFocus
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={onSaveEdit}
              className="h-8 w-8"
            >
              ✓
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onCancelEdit}
              className="h-8 w-8"
            >
              <X size={16} />
            </Button>
          </>
        ) : (
          <>
            <span className="flex-1 font-medium">{context.name}</span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(context)}
              className="h-8 w-8 text-muted-foreground hover:text-black"
            >
              <Pencil size={16} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(context)}
              className="h-8 w-8 text-muted-foreground hover:text-black"
            >
              <Trash2 size={16} />
            </Button>
          </>
        )}
        {dragHandlePosition === 'right' && dragHandle}
      </div>
      {!isEditing && (
        <div className="pl-1">
          <LinkedListPicker
            currentListId={context.active_list_id || null}
            activeLists={activeLists}
            onChange={(listId) => onLinkChange(context, listId)}
            testIdPrefix={`context-link-${context.id}`}
          />
        </div>
      )}
    </div>
  );
}

export default function EditContextsDialog({ open, onOpenChange, onContextsChanged }) {
  const { fetchContexts, dragHandlePosition } = useSettings();
  const [contexts, setContexts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [pendingChanges, setPendingChanges] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [activeLists, setActiveLists] = useState([]);

  useEffect(() => {
    if (open) {
      loadContexts();
      // Fetch active lists for the linked-to picker
      axios.get(`${API}/active_lists`, { withCredentials: true })
        .then(r => setActiveLists(r.data || []))
        .catch(() => setActiveLists([]));
    }
  }, [open]);

  const handleLinkChange = async (context, listId) => {
    try {
      await axios.patch(
        `${API}/contexts/${context.id}`,
        { active_list_id: listId == null ? '' : listId },
        { withCredentials: true }
      );
      setContexts(prev => prev.map(c => c.id === context.id ? { ...c, active_list_id: listId } : c));
      toast.success(listId ? 'Context linked' : 'Context unlinked');
    } catch (error) {
      console.error('Error linking context:', error);
      toast.error('Failed to update link');
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const loadContexts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/contexts`, { withCredentials: true });
      setContexts(response.data);
      setPendingChanges(false);
    } catch (error) {
      console.error('Error loading contexts:', error);
      toast.error('Failed to load contexts');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setContexts((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
      setPendingChanges(true);
    }
  };

  const handleEdit = (context) => {
    setEditingId(context.id);
    setEditValue(context.name);
  };

  const handleSaveEdit = async () => {
    if (!editValue.trim()) {
      toast.error('Context name cannot be empty');
      return;
    }

    const contextToEdit = contexts.find(c => c.id === editingId);
    if (contextToEdit.name !== editValue.trim()) {
      setContexts(prev => prev.map(c => 
        c.id === editingId ? { ...c, name: editValue.trim() } : c
      ));
      setPendingChanges(true);
    }
    
    setEditingId(null);
    setEditValue('');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const handleDelete = (context) => {
    if (contexts.length <= 1) {
      toast.error('Cannot delete the last context');
      return;
    }
    setShowDeleteConfirm(context);
  };

  // Helper to extract error message from API response
  const getErrorMessage = (error, fallback) => {
    const detail = error.response?.data?.detail;
    if (!detail) return fallback;
    // Handle Pydantic validation errors (array of objects with msg property)
    if (Array.isArray(detail)) {
      return detail.map(e => e.msg || JSON.stringify(e)).join(', ');
    }
    // Handle string error messages
    if (typeof detail === 'string') return detail;
    // Handle object with msg property
    if (detail.msg) return detail.msg;
    // Fallback for unknown object structure
    return fallback;
  };

  const confirmDelete = async () => {
    if (!showDeleteConfirm) return;

    try {
      await axios.delete(`${API}/contexts/${showDeleteConfirm.id}`, { withCredentials: true });
      setContexts(prev => prev.filter(c => c.id !== showDeleteConfirm.id));
      toast.success('Context deleted');
      setShowDeleteConfirm(null);
      // Refresh global contexts
      fetchContexts();
      if (onContextsChanged) onContextsChanged();
    } catch (error) {
      console.error('Error deleting context:', error);
      toast.error(getErrorMessage(error, 'Failed to delete context'));
    }
  };

  const handleAddContext = async () => {
    if (contexts.length >= 5) {
      toast.error('Maximum of 5 contexts allowed');
      return;
    }

    try {
      const newPriority = contexts.length + 1;
      const response = await axios.post(`${API}/contexts`, {
        name: `Context ${newPriority}`,
        priority: newPriority
      }, { withCredentials: true });
      
      setContexts(prev => [...prev, response.data]);
      toast.success('Context added');
      // Refresh global contexts
      fetchContexts();
      if (onContextsChanged) onContextsChanged();
    } catch (error) {
      console.error('Error adding context:', error);
      toast.error(getErrorMessage(error, 'Failed to add context'));
    }
  };

  const handleConfirm = async () => {
    if (!pendingChanges) {
      onOpenChange(false);
      return;
    }

    setSaving(true);
    let saveSuccessful = false;
    
    try {
      // Save name changes for each context
      for (const context of contexts) {
        await axios.put(`${API}/contexts/${context.id}`, {
          name: context.name
        }, { withCredentials: true });
      }

      // Save order
      const contextIds = contexts.map(c => c.id);
      await axios.put(`${API}/contexts/reorder`, { context_ids: contextIds }, { withCredentials: true });

      saveSuccessful = true;
      toast.success('Contexts saved');
    } catch (error) {
      console.error('Error saving contexts:', error);
      toast.error(getErrorMessage(error, 'Failed to save contexts'));
    }
    
    setSaving(false);
    
    if (saveSuccessful) {
      setPendingChanges(false);
      onOpenChange(false);
      
      // Refresh global contexts after dialog closes
      setTimeout(async () => {
        try {
          if (fetchContexts) await fetchContexts();
          if (onContextsChanged) onContextsChanged();
        } catch (refreshError) {
          console.error('Error refreshing contexts after save:', refreshError);
        }
      }, 100);
    }
  };

  const handleCancel = () => {
    if (pendingChanges) {
      // Reset to original
      loadContexts();
    }
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <DialogTitle className="text-xl">Edit Contexts</DialogTitle>
              <HelpDialog 
                title="About Contexts" 
                content={HELP_CONTENT}
                size="large"
              />
            </div>
          </DialogHeader>

          <div>
            {/* Add Context button below header on the left */}
            <div className="flex justify-start mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddContext}
                disabled={contexts.length >= 5}
                className="flex items-center gap-1"
              >
                <Plus size={16} />
                Add Context
              </Button>
            </div>
            
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : (
              <div className="space-y-2">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext items={contexts.map(c => c.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2">
                      {contexts.map((context) => (
                        <SortableContextItem
                          key={context.id}
                          context={context}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                          isEditing={editingId === context.id}
                          editValue={editValue}
                          setEditValue={setEditValue}
                          onSaveEdit={handleSaveEdit}
                          onCancelEdit={handleCancelEdit}
                          dragHandlePosition={dragHandlePosition}
                          activeLists={activeLists}
                          onLinkChange={handleLinkChange}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>

                {contexts.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No contexts found. Add one to get started.
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="gap-3 sm:space-x-0">
            <Button variant="outline" onClick={handleCancel} disabled={saving}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirm} 
              disabled={saving}
              data-testid="confirm-contexts-button"
              style={{ backgroundColor: 'var(--color-primary)', color: 'white' }}
            >
              {saving ? 'Saving...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Context?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-4">
            Are you sure you want to delete "{showDeleteConfirm?.name}"? 
            All tasks in this context will be moved to the first remaining context.
            All tags in this context will be deleted.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDelete}
              data-testid="delete-context-confirm-button"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
